<template>
  <div class="call-requests-index">
    <Breadcrumbs
      :breadcrumbs="[
        { title: this.$t(`menu.${this.serviceSlug}`), disabled: true },
        { title: this.$t('evocall.requests.title'), disabled: true }
      ]">

      <!-- Commented for now since it's not doing any search. Will be handled later
      <div slot="breadcrumbAfter">
        <Searchable :querySearch.sync="querySearch" :searchTooltip="{placement: 'left', content: this.$t('evocall.requests.search_tooltip')}" />
      </div>-->
    </Breadcrumbs>

    <div>
      <Dashboard
        ref="metrics"
        class="mb-5"
        :requests="requests"
        :waitingRoomModuleKey="waitingRoomModuleKey"
      />

      <v-client-table
        id="requestsTable"
        ref="requestsTable"
        name="referencesTable"
        :data="filteredRequests"
        :columns="columns"
        :options="requestsTableOptions"
        @row-click="displayConfirmationModal"
        @filter="filterListener">

        <span slot="h__locked"></span>

        <span v-show="row.openedBy !== null" slot="locked" slot-scope="{ row }">
          <span class="i-lock"></span>
        </span>

        <span slot="created_at" class="no-break" slot-scope="{ row }">
          {{ row.created_at | localTime('YYYY-MM-DD HH:mm') }}
        </span>

        <span v-if="row.openedBy !== null " slot="priority" slot-scope="{ row }" class="no-break" style="font-weight: 600">
          {{row.openedBy.nickname || row.openedBy.name}}
        </span>

        <span v-else slot="priority" class="no-break" slot-scope="{ row }" @click.stop="updatePriority(row)">
          <va-popover
              offset="16"
              placement="left"
              :popoverClasses="[`priority--${row.priority.name.toLowerCase()}`]"
              :popoverStyles="{'border-radius': 0}"
              :backgroundColor="priorities[row.priority.id].color"
            >
              <span
                :class="[
                  `priority-icon priority--${row.priority.name.toLowerCase()}`,
                  priorities[row.priority.id].iconClass
                ]">
              </span>

              <PriorityDetails
                slot="popover"
                :request="row" />

            </va-popover>
        </span>

        <span slot = "waiting_time" class="no-break" slot-scope="{ row }">
          {{ row.estimated_accept_time | localTime('YYYY-MM-DD HH:mm') }}
        </span>

        <span slot="creator.name" class="no-break" slot-scope="{ row }">
          <span v-if="row.creator">
            {{ row.creator.nickname || row.creator.name }}
          </span>
        </span>

        <span slot="type.name" class="no-break" slot-scope="{ row }">
          {{ $t(`evocall.types.${row.type.id}`, row.type.name) }}
        </span>

        <span slot="accountable_type" class="no-break" slot-scope="{ row }">
          {{ $t(`evocall.accountable_types.${row.accountable_type.toLowerCase()}`, row.accountable_type) }}
        </span>

        <span slot="seen" class="no-break" slot-scope="{ row }">
          <span v-if="row.person && row.person.seenBy.includes($auth.user().id)" class="fa fa-check"></span>
        </span>

        <span slot="obs" slot-scope="{row}" @click.stop="updateObservations(row)">
          <div class="clickable d-flex justify-content-center">
            <va-popover v-if="row.obs"
              :message="row.obs"
              placement="left"
            >
              <i class="i-has-comment-accent"></i>
            </va-popover>

            <span v-else>
              <i class="i-add-comment-gray"></i>
            </span>
          </div>
        </span>

      </v-client-table>

        <RequestDetails
          v-if="showConfirmationModal"
          :request="selectedRequest"
          :service-slug="serviceSlug"
          :component="component"
          @modal-closed="closeConfirmationModal"
          @priority-updated="priorityUpdatedFromDetailsModal"
          @observations-updated="observationsUpdatedFromDetailsModal" />

        <ObservationsForm
          v-if="showObservationsModal"
          :request="selectedRequest"
          @modal-closed="updateModalClosed"
          @observations-updated="observationsUpdated" />

        <PriorityForm
          v-if="showPriorityModal"
          :request="selectedRequest"
          @modal-closed="updateModalClosed"
          @priority-updated="priorityUpdated" />
    </div>
  </div>
</template>
<script>

import Vue from 'vue'
import { mapState } from 'vuex'
import Dashboard from './Dashboard'
import PriorityForm from './PriorityForm'
import { ClientTable } from 'vue-tables-2'
import RequestDetails from './RequestDetails'
import PriorityDetails from './PriorityDetails'
import ObservationsForm from './ObservationsForm'
import Searchable from '@/components/common/Searchable'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import * as _ from 'lodash'

Vue.use(ClientTable, {}, false, 'bootstrap4')

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  components: {
    Breadcrumbs,
    Dashboard,
    Searchable,
    PriorityForm,
    PriorityDetails,
    ObservationsForm,
    RequestDetails
  },
  data () {
    return {
      querySearch: '',
      selectedRequest: null,
      showConfirmationModal: false,
      filterListener: '',
      showResetIcon: false,
      requestsTableOptions: {
        requestFunction: (data) => {
          return this.$http.get(`${this.$store.state.evocall_server}/requests`, {
            params: data
          }).catch(function (e) {
            this.dispatch('error', e)
          }.bind(this))
        },
        responseAdapter: function (resp) {
          let data = this.getResponseData(resp)

          return {
            data: data.data.data,
            count: data.data.meta.total
          }
        },
        rowClassCallback: (row) => {
          if (row.opened_by !== null && row.openedBy.id !== this.authUser.id) {
            return 'blocked-row'
          }
        },
        filterable: false,
        texts: {
          count: this.$t('tables.records') + ' {from} ' + this.$t('tables.to') + ' {to} ' + this.$t('tables.of') + ' {count}|{count} ' + this.$t('tables.records_') + '|' + this.$t('tables.one_record'),
          first: this.$t('tables.first'),
          last: this.$t('tables.last'),
          filter: this.$t('tables.filter') + ':',
          filterPlaceholder: this.$t('tables.search_text'),
          limit: this.$t('tables.records') + ':',
          page: this.$t('tables.page') + ':',
          noResults: this.$t('evocall.requests.empty_queue'),
          filterBy: this.$t('tables.filter_by') + ' {column}',
          loading: this.$t('tables.loading') + '...',
          defaultOption: this.$t('tables.select') + ' {column}',
          columns: this.$t('tables.columns_dropdown'),
        },
        sortable: ['updated_at', 'name', 'email', 'created_at', 'status'],
        headings: {
          'priority': this.$t('evocall.requests.priority'),
          'waiting_time': this.$t('evocall.requests.estimated_accept_time'),
          'person.name': this.$t('evocall.requests.person_name'),
          'entity.name': this.$t('evocall.requests.entity'),
          'facility.name': this.$t('evocall.requests.facility'),
          'activity.name': this.$t('evocall.requests.activity'),
          'accepted.name': this.$t('evocall.requests.accepted'),
          'source.name': this.$t('evocall.requests.source'),
          'type.name': this.$t('evocall.requests.type'),
          'created_at': this.$t('evocall.requests.created_at'),
          'creator.name': this.$t('evocall.requests.creator'),
          'seen': this.$t('evocall.requests.already_seen'),
          'obs': this.$t('evocall.requests.obs')
        },
        skin: 'table table-striped table-hover is-fullwidth',
        customFilters: ['entity'],
        debounce: 500,
        columnsDropdown: false,
        columnsClasses: {
          locked: 'locked',
          name: 'name',
          phone: 'phone',
          email: 'email',
          entity: 'entity',
          priority: 'priority',
          waiting_time: 'text-center',
          status: 'status',
          info: 'info',
          action: 'action',
          updated_at: 'updated_at',
          seen: 'seen',
          obs: 'obs'
        },
        sortIcon: {
          is: 'sort-icon',
          base: 'sort-icon-class',
          up: 'sort-icon-up',
          down: 'sort-icon-down'
        },
        pagination: {
          chunk: 5,
          nav: 'scroll'
        },
        saveState: false,
        storage: 'local'
      },
      showObservationsModal: false,
      showPriorityModal: false,
    }
  },
  created () {
    this.$socket.emit('GET_QUEUE', { serviceId: this.component.clinical_service_id })
  },
  methods: {
    updateObservations: function (request) {
      if (!this.hasPermissionToEntity(request.entity.id, this.evocallInfoTypes.REQUESTS, this.accessTypes.WRITE)) {
        if (!this.hasPermissionToEntity(request.entity.id, this.evocallInfoTypes.REQUEST_PRIORITY, this.accessTypes.WRITE)) {
          return
        }
      }
      this.selectedRequest = request
      this.showObservationsModal = true
    },
    priorityUpdated: function () {
      this.showPriorityModal = false
      this.selectedRequest = null
    },
    observationsUpdated: function () {
      this.showObservationsModal = false
      this.selectedRequest = null
    },
    priorityUpdatedFromDetailsModal: function ({ request }) {
      this.selectedRequest = request
    },
    observationsUpdatedFromDetailsModal: function ({ request }) {
      this.selectedRequest = request
    },
    updatePriority: function (request) {
      if (this.hasPermissionToEntity(request.entity.id, this.evocallInfoTypes.REQUESTS, this.accessTypes.ADMIN)) {
        this.selectedRequest = request
        this.showPriorityModal = true
      }
    },
    // eslint-disable-next-line camelcase
    hasPermissionToEntity: function (entity_id, info_type, access_type) {
      // eslint-disable-next-line camelcase
      return this.evocallService.user_permissions.some(permission => permission.info_type === info_type && permission.access_type >= access_type && (permission.entity_id === entity_id || permission.entity_id === null))
    },
    updateModalClosed: function () {
      this.showObservationsModal = false
      this.showPriorityModal = false
      this.selectedRequest = null
    },
    displayConfirmationModal: function ({ row }) {
      if (row.opened_by === null || row.openedBy.id === this.authUser.id) {
        this.$socket.emit('REQUEST_OPENED', { serviceId: this.component.clinical_service_id, requestRow: row })
        this.selectedRequest = row
        this.showConfirmationModal = true
      }
    },
    closeConfirmationModal: function () {
      this.showConfirmationModal = false
    },
    refreshTable: function () {
      this.$refs.requestsTable.refresh()
    }
  },
  computed: {
    readRequestsPermissions: function () {
      return this.evocallService.user_permissions.filter(permission => this.evocallInfoTypes.REQUESTS === permission.info_type && this.accessTypes.READ <= permission.access_type)
    },
    columns: function () {
      if (this.readRequestsPermissions.length > 1) {
        return ['locked', 'created_at', 'person.name', 'entity.name', 'facility.name', 'creator.name', 'seen', 'priority', 'waiting_time', 'obs']
      } else {
        return ['locked', 'created_at', 'person.name', 'facility.name', 'creator.name', 'seen', 'priority', 'waiting_time', 'obs']
      }
    },
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.component.clinical_service.slug}`
    },
    ...mapState({
      authUser (state) {
        return state[`loggedUser`]
      },
      requests (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/sortedRequests`]
      },
      evocallInfoTypes (state, getters) {
        return state[this.waitingRoomModuleKey].evocallInfoTypes || null
      },
      accessTypes (state, getters) {
        return state[this.waitingRoomModuleKey].accessTypes || null
      },
      evocallService (state) {
        return state.services.find(service => service.url === state.evocall_server) || null
      },
      priorities (state) {
        return state[this.waitingRoomModuleKey].priorities || {}
      }
    }),
    filteredRequests: function () {
      if (!this.querySearch) {
        return this.requests
      }
      let search = this.querySearch.toLowerCase()
      return this.requests.filter(request => (request.person && request.person.name.toLowerCase().includes(search)) || (request.creator && request.creator.name.toLowerCase().includes(search)) || (request.obs && request.obs.toLowerCase().includes(search)))
    }
  }
}
</script>

<style lang="scss">

.call-requests-index {

  $primary-red: #c42940;
  $primary-yellow: #c49d29;
  $primary-green: #29c4aa;

  .tooltip.popover {
    &.priority--high {
      .popover-arrow {
        border-color: $primary-red;
      }
    }
    &.priority--normal {
      .popover-arrow {
        border-color: $primary-yellow;
      }
    }
    &.priority--low {
      .popover-arrow {
        border-color: $primary-green;
      }
    }
  }

  #requestsTable {

    i {
      display: block;
      width: 1.5rem;
      height: 1.5rem;
    }

    .priority, .obs, .seen, .locked{
      text-align: center;
    }
    tr:nth-of-type(odd).blocked-row {
      &:hover {
        td.priority{
          color: #6e6e6e;
        }
        background-color: #E8E8E8;
      }
    }

    tr:nth-of-type(even).blocked-row {

      &:hover {
        td.priority{
          color: #6e6e6e;
        }

        background-color: unset;
      }
    }

    .blocked-row {
      td:not(.priority){
        color: rgba(100, 100, 100, 0.25);
      }
      .i-add-comment-gray {
        @extend .i-add-comment-locked;
      }

      .i-has-comment-gray {
        @extend .i-has-comment-locked;
      }

      &:hover {
        .i-add-comment-gray {
          @extend .i-add-comment-locked;
        }

        .i-has-comment-gray {
          @extend .i-has-comment-locked;
        }
      }
    }

    $assetsPath: "../../../assets";
    @import '@/sass/icons/icons-classes.scss';

    tr:hover {
      .i-add-comment-gray {
        @extend .i-add-comment-white;
      }

      .i-has-comment-gray {
        @extend .i-has-comment-white;
      }
    }
  }
}

</style>
